import React from 'react'
import {
  CUSTOMER_DEFAULT_VALUE,
  PAYMENT_METHODS,
  SALES_TAX_STATUSES,
  customerDefaultOptionLabel,
  isPropertyUsingCustomerDefault,
  resolvePropertyPaymentMethod,
  resolvePropertySalesTaxStatus,
  type CustomerPaymentDefaults,
  type PropertyPaymentData,
} from './types'

interface PropertyPaymentFieldsProps {
  value: PropertyPaymentData
  onChange: (value: PropertyPaymentData) => void
  compact?: boolean
  customerDefaults?: CustomerPaymentDefaults | null
}

function effectiveValueHint(
  field: 'payment_method' | 'sales_tax_status',
  propertyValue: string,
  customerDefaults?: CustomerPaymentDefaults | null
): string | null {
  if (!isPropertyUsingCustomerDefault(propertyValue)) return null

  const label = field === 'payment_method' ? 'payment method' : 'sales tax status'
  const resolved =
    field === 'payment_method'
      ? resolvePropertyPaymentMethod(propertyValue, customerDefaults)
      : resolvePropertySalesTaxStatus(propertyValue, customerDefaults)

  if (resolved) {
    return `This property uses the customer's default ${label}: ${resolved}`
  }
  return `Customer Default is selected, but the customer has no default ${label} set.`
}

export function PropertyPaymentFields({
  value,
  onChange,
  compact,
  customerDefaults,
}: PropertyPaymentFieldsProps) {
  const inputClass = compact
    ? 'w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-sm'
    : 'w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500'

  const set = (patch: Partial<PropertyPaymentData>) => onChange({ ...value, ...patch })

  const paymentHint = effectiveValueHint('payment_method', value.payment_method, customerDefaults)
  const taxHint = effectiveValueHint('sales_tax_status', value.sales_tax_status, customerDefaults)

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Payment Method</label>
        <select
          value={value.payment_method ?? CUSTOMER_DEFAULT_VALUE}
          onChange={(e) => set({ payment_method: e.target.value })}
          className={inputClass}
        >
          <option value={CUSTOMER_DEFAULT_VALUE}>
            {customerDefaultOptionLabel('payment_method', customerDefaults)}
          </option>
          {PAYMENT_METHODS.map((method) => (
            <option key={method} value={method}>
              {method}
            </option>
          ))}
        </select>
        {paymentHint && (
          <p
            className={`text-sm mt-2 ${
              resolvePropertyPaymentMethod(value.payment_method, customerDefaults)
                ? 'text-blue-800 bg-blue-50 border border-blue-100 rounded-md px-3 py-2'
                : 'text-amber-800 bg-amber-50 border border-amber-100 rounded-md px-3 py-2'
            }`}
          >
            {paymentHint}
          </p>
        )}
        {!paymentHint && (
          <p className="text-xs text-gray-500 mt-1">
            Property-specific override. Choose Customer Default to inherit from the customer.
          </p>
        )}
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Sales Tax Status</label>
        <select
          value={value.sales_tax_status ?? CUSTOMER_DEFAULT_VALUE}
          onChange={(e) => set({ sales_tax_status: e.target.value })}
          className={inputClass}
        >
          <option value={CUSTOMER_DEFAULT_VALUE}>
            {customerDefaultOptionLabel('sales_tax_status', customerDefaults)}
          </option>
          {SALES_TAX_STATUSES.map((status) => (
            <option key={status} value={status}>
              {status}
            </option>
          ))}
        </select>
        {taxHint && (
          <p
            className={`text-sm mt-2 ${
              resolvePropertySalesTaxStatus(value.sales_tax_status, customerDefaults)
                ? 'text-blue-800 bg-blue-50 border border-blue-100 rounded-md px-3 py-2'
                : 'text-amber-800 bg-amber-50 border border-amber-100 rounded-md px-3 py-2'
            }`}
          >
            {taxHint}
          </p>
        )}
        {!taxHint && (
          <p className="text-xs text-gray-500 mt-1">
            Property-specific override. Choose Customer Default to inherit from the customer.
          </p>
        )}
      </div>
    </div>
  )
}
